<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="12">
        <question-text
          class="question-text"
          v-model="form.question"
          :disabled="isEditable"
          :requiredMessage="questionReq"
          :id="`question-${form.id}`"
        ></question-text>
      </v-col>
      <v-col cols="12" sm="12" md="12" v-if="form.isSubtitle">
        <question-text
          class="subtitle-1"
          :label="subtitleLabel"
          v-model="form.subtitle"
          :disabled="isEditable"
          :isSubtitle="form.isSubtitle"
          :requiredMessage="subtitleReq"
          :id="`question-subtitle-${form.id}`"
        ></question-text>
      </v-col>
    </v-row>
    <v-row>
      <v-simple-table style="width: 100%">
        <template v-slot:default>
          <thead>
            <tr>
              <th style="width: 200px"></th>
              <th
                class="text-left"
                v-for="(head, index) in headers"
                :key="head.value"
                style="width: 350px"
              >
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-hover v-slot="{ hover }">
                      <v-text-field
                        v-model.trim="head.text"
                        hide-details
                        :placeholder="`${$t('poll.lblOption')} ${index + 1}`"
                        @focus="$event.target.select()"
                        @blur="onBlurOption(head, index + 1)"
                        class="likert-input mb-2 likertOptionField"
                        :class="
                          hover ? 'likert-border-solid' : 'likert-border-none'
                        "
                        style="font-size: 14px; font-weight: 500"
                        :disabled="isEditable"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                v-show="hover"
                                small
                                icon
                                @click.stop.prevent="onDeleteOption(head.value)"
                              >
                                <v-icon color="primaryDark" size="18"
                                  >mdi-delete</v-icon
                                >
                              </v-btn>
                            </template>
                            <span>{{ $t("btnDelete") }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-hover>
                  </v-col>
                </v-row>
              </th>
              <th style="width: 50px">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      v-bind="attrs"
                      v-on="on"
                      @click="onAddOption()"
                      v-if="!isEditable"
                      color="primary"
                      ><v-icon>mdi-plus</v-icon></v-btn
                    >
                  </template>
                  <span> {{ $t("poll.tipAddOption") }}</span>
                </v-tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in items" :key="index">
              <td style="width: 200px">
                <v-hover v-slot="{ hover }">
                  <v-text-field
                    v-model.trim="item.text"
                    hide-details
                    :placeholder="`${$t('poll.lblStatement')} ${index + 1}`"
                    @focus="$event.target.select()"
                    @blur="onBlurStatement(item, index + 1)"
                    class="likert-input mb-2 likertStatementField"
                    :class="
                      hover ? 'likert-border-solid' : 'likert-border-none'
                    "
                    height="28px"
                    :disabled="isEditable"
                  >
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            v-show="hover"
                            small
                            icon
                            @click.stop.prevent="onDeleteStatement(item.text)"
                          >
                            <v-icon color="primaryDark" size="18"
                              >mdi-delete</v-icon
                            >
                          </v-btn>
                        </template>
                        <span>{{ $t("btnDelete") }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-hover>
              </td>
              <td v-for="head in options" :key="head.text">
                <v-radio-group
                  dense
                  v-model="item.value"
                  row
                  hide-details
                  class="mt-0 pb-1"
                  readonly
                >
                  <v-radio :value="head.value"></v-radio>
                </v-radio-group>
              </td>
            </tr>
            <tr v-if="!isEditable">
              <td>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="ml-5"
                      v-bind="attrs"
                      v-on="on"
                      small
                      text
                      @click="onAddStatement"
                      color="primary"
                      ><v-icon>mdi-plus</v-icon
                      >{{ $t("poll.btnAddStatement") }}</v-btn
                    >
                  </template>
                  <span>{{ $t("poll.btnAddStatement") }}</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-row>
    <question-settings v-model="form" v-if="!isEditable"></question-settings>
  </div>
</template>

<script>
import QuestionText from "./QuestionText.vue";
import { createUUID } from "../../../utils/util";
import MultipleSwitch from "./MultipleSwitch.vue";
import QuestionSettings from "./QuestionSettings.vue";
import { mapGetters, mapState } from "vuex";
export default {
  components: { QuestionText, MultipleSwitch, QuestionSettings },
  name: "Likert",
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    responseQuestion: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      headers: [],
      items: [],
      mandatoryLabel: this.$t("poll.lblMandatoryResponse"),
      question: this.value,
    };
  },
  computed: {
    ...mapState({
      surveyDetails: (state) => state.poll.surveyDetails,
    }),
    ...mapGetters({
      isSurveyExpired: "poll/isSurveyExpired",
    }),
    form: {
      get() {
        return this.question;
      },
      set(value) {
        this.question = value;
        this.$emit("input", value);
      },
    },
    options() {
      return this.form.options.options;
    },
    statement() {
      return this.form.options.statement;
    },
    subtitleLabel() {
      return this.$t("poll.lblSubtitle");
    },
    questionReq() {
      return this.$t("msgQuestionRequired");
    },
    subtitleReq() {
      return this.$t("msgSubtitleRequired");
    },
    isEditable() {
      if (this.isSurveyExpired) {
        return true;
      }
      const result = this.responseQuestion.find(
        (item) => item.questionId === this.question.id
      );
      if (result) {
        return result.answer.some((a) => a.value !== null);
      }else{
        return false
      }
    },
  },
  mounted() {
    this.headers = this.options;
    this.items = this.statement;
  },
  methods: {
    createUUID,
    onAddOption,
    onDeleteOption,
    onDeleteStatement,
    onAddStatement,
    omMouseOver,
    onBlurStatement,
    onBlurOption,
  },
};

function onBlurStatement(item, index) {
  if (item.text === "") {
    item.text = `${this.$t("poll.lblStatement")} ${index}`;
  }
}

function onBlurOption(item, index) {
  if (item.text === "") {
    item.text = `${this.$t("poll.lblOption")} ${index}`;
  }
}

function omMouseOver(head) {
  head.isHover = true;
}

function onAddStatement() {
  this.statement.push({
    text: `${this.$t("poll.lblStatement")} ${this.statement.length + 1}`,
    value: null,
  });
}

function onDeleteStatement(text) {
  this.statement.splice(
    this.statement.findIndex((a) => a.text === text),
    1
  );
}

function onDeleteOption(value) {
  this.options.splice(
    this.options.findIndex((a) => a.value === value),
    1
  );
}

function onAddOption() {
  this.options.push({
    text: `${this.$t("poll.lblOption")} ${this.options.length + 1}`,
    value: createUUID(),
    isActive: false,
  });
}
</script>

<style scoped>
.likertOptionField {
  line-height: 18px !important;
}
.likertStatementField {
  line-height: 18px !important;
}
.likert-border-none >>> .v-input__slot::before {
  border-style: none !important;
}

.likert-border-solid >>> .v-input__slot:before {
  border-style: solid;
}

tbody tr:hover {
  background-color: transparent !important;
}
</style>